import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = "admin" | "moderator" | "user";
export type Department =
  | "admin"
  | "editor"
  | "graphic_designer"
  | "ai"
  | "marketer"
  | "seo"
  | "smm"
  | "scriptwriter"
  | "calling"
  | "hr";

export interface Profile {
  id: string;
  email: string;
  full_name: string;
  avatar_url: string | null;
  department: Department;
  monthly_target: number;
  status: string;
  moderator_permissions: Record<string, unknown>;
  can_add_vault_entries?: boolean;
  mfa_enabled?: boolean;
  is_team_lead?: boolean;
}

interface AuthContextValue {
  loading: boolean;
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  role: AppRole | null;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = async (uid: string) => {
    const [{ data: p }, { data: r }] = await Promise.all([
      supabase.from("profiles").select("*").eq("id", uid).maybeSingle(),
      supabase.from("user_roles").select("role").eq("user_id", uid),
    ]);
    setProfile((p as Profile | null) ?? null);
    const roles = (r ?? []).map((x: { role: AppRole }) => x.role);
    setRole(
      roles.includes("admin")
        ? "admin"
        : roles.includes("moderator")
          ? "moderator"
          : roles.includes("user")
            ? "user"
            : null,
    );
  };

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_e, sess) => {
      setSession(sess);
      setUser(sess?.user ?? null);
      if (sess?.user) {
        setTimeout(() => loadProfile(sess.user.id), 0);
      } else {
        setProfile(null);
        setRole(null);
      }
    });
    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      if (data.session?.user) await loadProfile(data.session.user.id);
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  };
  const signOut = async () => {
    if (typeof window !== "undefined") sessionStorage.removeItem("mfa_passed");
    await supabase.auth.signOut();
  };
  const refresh = async () => {
    if (user) await loadProfile(user.id);
  };

  return (
    <AuthContext.Provider
      value={{ loading, user, session, profile, role, signIn, signOut, refresh }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}

export const DEPARTMENT_LABELS: Record<Department, string> = {
  admin: "Admin",
  editor: "Editor",
  graphic_designer: "Graphic Designer",
  ai: "AI",
  marketer: "Marketer",
  seo: "SEO",
  smm: "SMM",
  scriptwriter: "Scriptwriter",
  calling: "Calling",
  hr: "HR",
};
